<template>
    <div class="monthly-statistics-view">
        <v-main>
            <year-month-range-picker v-model="period" @valid="onValid" @reload="load">
                <template #actions>
                    <v-btn color="primary" :loading="loading" :disabled="!valid" @click="load"> Oppdater </v-btn>
                </template>
            </year-month-range-picker>

            <template v-if="store.byDayLineDeparture && store.byDayLineDeparture.length > 0">
                <v-row>
                    <v-col>
                        <table class="monthly-table" aria-label="Monthly statistics">
                            <thead>
                                <tr>
                                    <td></td>
                                    <th class="headers-title" colspan="2" scope="colgroup">Passasjerer</th>
                                    <th class="headers-title" colspan="2" scope="colgroup">Kjøretøy</th>
                                    <td></td>
                                </tr>
                                <tr>
                                    <th class="headers-title">Måned / Linje</th>
                                    <th class="headers-title">Inn</th>
                                    <th class="headers-title">Ut</th>
                                    <th class="headers-title">Inn</th>
                                    <th class="headers-title">Ut</th>
                                    <th class="headers-title">Kjøretøy etterlatt</th>
                                </tr>
                            </thead>
                            <tbody>
                                <template v-for="m in monthsSummary" :key="m.month">
                                    <tr class="month-summary">
                                        <td>{{ formatYearMonth(m.month) }}</td>
                                        <td>{{ m.passengersIn }}</td>
                                        <td>{{ m.passengersOut }}</td>
                                        <td>{{ m.carsIn }}</td>
                                        <td>{{ m.carsOut }}</td>
                                        <td>{{ m.carsLeftBehind }}</td>
                                    </tr>
                                    <tr v-for="line in m.lines" :key="`${m.month}-${line.lineNumber}`" class="line-row">
                                        <td>{{ line.lineName }}</td>
                                        <td>{{ line.passengersIn }}</td>
                                        <td>{{ line.passengersOut }}</td>
                                        <td>{{ line.carsIn }}</td>
                                        <td>{{ line.carsOut }}</td>
                                        <td>{{ line.carsLeftBehind }}</td>
                                    </tr>
                                </template>
                            </tbody>
                            <tfoot>
                                <tr>
                                    <td><strong>Sum</strong></td>
                                    <td>
                                        <strong>{{ totals.passengersIn }}</strong>
                                    </td>
                                    <td>
                                        <strong>{{ totals.passengersOut }}</strong>
                                    </td>
                                    <td>
                                        <strong>{{ totals.carsIn }}</strong>
                                    </td>
                                    <td>
                                        <strong>{{ totals.carsOut }}</strong>
                                    </td>
                                    <td>
                                        <strong>{{ totals.carsLeftBehind }}</strong>
                                    </td>
                                </tr>
                            </tfoot>
                        </table>
                    </v-col>
                </v-row>
            </template>
            <v-row v-else-if="loaded">
                <v-col class="text-center"><em>Ingen data for valgt periode.</em></v-col>
            </v-row>
        </v-main>
    </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import YearMonthRangePicker from './components/YearMonthRangePicker.vue';
import { formatYearMonth } from './monthFormat';
import { useStatisticsStore } from './statisticsStore';

interface MonthLineSummary {
    lineNumber: string;
    lineName: string;
    passengersIn: number;
    passengersOut: number;
    carsIn: number;
    carsOut: number;
    carsLeftBehind: number;
}

interface MonthSummary {
    month: string; // "YYYY-MM"
    passengersIn: number;
    passengersOut: number;
    carsIn: number;
    carsOut: number;
    carsLeftBehind: number;
    lines: MonthLineSummary[];
}

const store = useStatisticsStore();
const period = ref({ from: store.monthlyPeriod.from, to: store.monthlyPeriod.to });
const valid = ref(true);
const loading = ref(false);
const loaded = ref(false);

const monthsSummary = computed<MonthSummary[]>(() => {
    const rows = store.byDayLineDeparture ?? [];
    const byMonth = new Map<string, MonthSummary>();
    for (const r of rows) {
        const month = r.day.slice(0, 7);
        let m = byMonth.get(month);
        if (!m) {
            m = {
                month,
                passengersIn: 0,
                passengersOut: 0,
                carsIn: 0,
                carsOut: 0,
                carsLeftBehind: 0,
                lines: []
            };
            byMonth.set(month, m);
        }
        m.passengersIn += r.passengersIn;
        m.passengersOut += r.passengersOut;
        m.carsIn += r.carsIn;
        m.carsOut += r.carsOut;
        m.carsLeftBehind += r.carsLeftBehind;

        let line = m.lines.find(l => l.lineNumber === r.lineNumber);
        if (!line) {
            line = {
                lineNumber: r.lineNumber,
                lineName: r.lineName,
                passengersIn: 0,
                passengersOut: 0,
                carsIn: 0,
                carsOut: 0,
                carsLeftBehind: 0
            };
            m.lines.push(line);
        }
        line.passengersIn += r.passengersIn;
        line.passengersOut += r.passengersOut;
        line.carsIn += r.carsIn;
        line.carsOut += r.carsOut;
        line.carsLeftBehind += r.carsLeftBehind;
    }
    const months = [...byMonth.values()].sort((a, b) => a.month.localeCompare(b.month));
    for (const m of months) {
        m.lines.sort((a, b) => a.lineNumber.localeCompare(b.lineNumber, undefined, { numeric: true }));
    }
    return months;
});

const totals = computed(() => {
    return monthsSummary.value.reduce(
        (acc, m) => ({
            passengersIn: acc.passengersIn + m.passengersIn,
            passengersOut: acc.passengersOut + m.passengersOut,
            carsIn: acc.carsIn + m.carsIn,
            carsOut: acc.carsOut + m.carsOut,
            carsLeftBehind: acc.carsLeftBehind + m.carsLeftBehind
        }),
        { passengersIn: 0, passengersOut: 0, carsIn: 0, carsOut: 0, carsLeftBehind: 0 }
    );
});

function onValid(v: boolean) {
    valid.value = v;
}

async function load() {
    if (!valid.value) return;
    loading.value = true;
    try {
        await store.fetchByDayLineDeparture(period.value.from, period.value.to);
        loaded.value = true;
    } finally {
        loading.value = false;
    }
}

onMounted(() => {
    load();
});
</script>

<style scoped>
.monthly-table {
    width: 100%;
    border-collapse: collapse;
    margin-top: 12px;
}
.monthly-table th,
.monthly-table td {
    padding: 8px 12px;
    text-align: center;
}
/* keep only the Måned / Linje column (month labels + line names) left-aligned;
   centering every other column makes the Passasjerer/Kjøretøy group labels sit
   centered over their column pairs */
.monthly-table th:first-child,
.monthly-table td:first-child {
    text-align: left;
}
/* thick line under the column-header row only (no border under Passasjerer/Kjøretøy) */
.monthly-table thead tr:nth-child(2) th {
    border-bottom: 2px solid rgba(0, 0, 0, 0.6);
    color: rgba(0, 0, 0, 0.6);
}
/* light separators between day rows */
.monthly-table tbody td {
    border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}
/* separate the sum row */
.monthly-table tfoot td {
    border-top: 2px solid rgba(0, 0, 0, 0.6);
}
.monthly-table .month-summary td {
    font-weight: bold;
    border-top: 2px solid rgba(0, 0, 0, 0.3);
    background-color: #eeeeee;
}
.monthly-table .line-row td:first-child {
    padding-left: 28px;
}
</style>
